import { customizeError, formatDate, roundWithPrecision } from '@chilecompra/react-kit'
import { makeActionCreator } from '../../config/store/utils'
import { formatNumberWithThousandSeparator } from '../../modules/utils/formatters'
import { checkIfForeignDni } from '../../modules/utils/helpers'
import {
  getSanctionsByProcessType,
  getContractualBehaviorSummary,
  getContractualBehaviorSummaryByReviews
} from '../../services/sanction'

export const GET_CONTRACTUAL_BEHAVIOR_SUMMARY = 'GET_CONTRACTUAL_BEHAVIOR_SUMMARY'
export const GET_CONTRACTUAL_BEHAVIOR_SUMMARY_ERROR = 'GET_CONTRACTUAL_BEHAVIOR_SUMMARY_ERROR'
export const GET_CONTRACTUAL_BEHAVIOR_SUMMARY_SUCCESS = 'GET_CONTRACTUAL_BEHAVIOR_SUMMARY_SUCCESS'
export const onGetContractualBehaviorSummary = makeActionCreator(GET_CONTRACTUAL_BEHAVIOR_SUMMARY)
export const onGetContractualBehaviorSummaryError = makeActionCreator(GET_CONTRACTUAL_BEHAVIOR_SUMMARY_ERROR, 'payload')
export const onGetContractualBehaviorSummarySuccess = makeActionCreator(
  GET_CONTRACTUAL_BEHAVIOR_SUMMARY_SUCCESS,
  'payload'
)
export const onGetContractualBehaviorSummaryThunk = payload => async dispatch => {
  dispatch(onGetContractualBehaviorSummary())

  try {
    const isForeign = checkIfForeignDni(payload.dni)
    const { data } = await getContractualBehaviorSummary({
      actionType: GET_CONTRACTUAL_BEHAVIOR_SUMMARY,
      dni: payload.dni,
      isForeign: isForeign ? 1 : 0
    })

    return dispatch(
      onGetContractualBehaviorSummarySuccess({
        dni: payload.dni,
        hasNoRate: data?.payload?.notabase === -1,
        rate: data?.payload?.notabase,
        max: 5,
        months: data?.payload?.mesesconsiderados,
        percentage: roundWithPrecision(data?.payload?.porcentajebase, 1).toString().replace('.', ','),
        purchaseOrders: formatNumberWithThousandSeparator(data?.payload?.ocaceptadas),
        nonComplianceSanctions: formatNumberWithThousandSeparator(data?.payload?.sancionesrecibidas),
        voidedSanctions: formatNumberWithThousandSeparator(data?.payload?.sancionesanuladas || 0)
      })
    )
  } catch (error) {
    let reason
    switch (error.code) {
      case 401:
        reason = 'UNAUTHORIZED_ERROR'
        break
      case 403:
        reason = 'FORBIDDEN_ERROR'
        break
      default:
        reason = 'SOMETHING_WENT_WRONG_ERROR'
        break
    }

    return dispatch(
      onGetContractualBehaviorSummaryError({
        error: customizeError({ originalError: error?.originalError, code: error?.code, reason }).toObject()
      })
    )
  }
}

export const GET_CONTRACTUAL_BEHAVIOR_SUMMARY_BY_REVIEWS = 'GET_CONTRACTUAL_BEHAVIOR_SUMMARY_BY_REVIEWS'
export const GET_CONTRACTUAL_BEHAVIOR_SUMMARY_BY_REVIEWS_ERROR = 'GET_CONTRACTUAL_BEHAVIOR_SUMMARY_BY_REVIEWS_ERROR'
export const GET_CONTRACTUAL_BEHAVIOR_SUMMARY_BY_REVIEWS_SUCCESS = 'GET_CONTRACTUAL_BEHAVIOR_SUMMARY_BY_REVIEWS_SUCCESS'
export const onGetContractualBehaviorSummaryByReviews = makeActionCreator(GET_CONTRACTUAL_BEHAVIOR_SUMMARY_BY_REVIEWS)
export const onGetContractualBehaviorSummaryByReviewsError = makeActionCreator(
  GET_CONTRACTUAL_BEHAVIOR_SUMMARY_BY_REVIEWS_ERROR,
  'payload'
)
export const onGetContractualBehaviorSummaryByReviewsSuccess = makeActionCreator(
  GET_CONTRACTUAL_BEHAVIOR_SUMMARY_BY_REVIEWS_SUCCESS,
  'payload'
)
export const onGetContractualBehaviorSummaryByReviewsThunk = payload => async dispatch => {
  dispatch(onGetContractualBehaviorSummaryByReviews())

  try {
    const { data } = await getContractualBehaviorSummaryByReviews({
      dni: payload.dni
    })

    return dispatch(
      onGetContractualBehaviorSummaryByReviewsSuccess({
        receivedContracts: data?.payload?.cantidadContratos,
        rateByReviews: data?.payload?.promedio,
        reviewedContracts: data?.payload?.cantidadNotas,
        reviewedPercentage: data?.payload?.porcentajeEvaluado
      })
    )
  } catch (error) {
    let reason
    switch (error.code) {
      case 401:
        reason = 'UNAUTHORIZED_ERROR'
        break
      case 403:
        reason = 'FORBIDDEN_ERROR'
        break
      default:
        reason = 'SOMETHING_WENT_WRONG_ERROR'
        break
    }

    return dispatch(
      onGetContractualBehaviorSummaryByReviewsError({
        error: customizeError({ originalError: error?.originalError, code: error?.code, reason }).toObject()
      })
    )
  }
}

export const GET_ALL_SANCTIONS = 'GET_ALL_SANCTIONS'
export const GET_ALL_SANCTIONS_ERROR = 'GET_ALL_SANCTIONS_ERROR'
export const GET_ALL_SANCTIONS_SUCCESS = 'GET_ALL_SANCTIONS_SUCCESS'
export const onGetAllSanctions = makeActionCreator(GET_ALL_SANCTIONS)
export const onGetAllSanctionsError = makeActionCreator(GET_ALL_SANCTIONS_ERROR, 'payload')
export const onGetAllSanctionsSuccess = makeActionCreator(GET_ALL_SANCTIONS_SUCCESS, 'payload')
export const onGetAllSanctionsThunk = payload => async dispatch => {
  dispatch(onGetAllSanctions())

  try {
    const isForeign = checkIfForeignDni(payload.dni)
    const { data } = await getSanctionsByProcessType({
      actionType: GET_ALL_SANCTIONS,
      dni: payload.dni,
      isForeign: isForeign ? 1 : 0,
      processType: 'all'
    })

    const [tenders, frameworkAgreement, directDeals, agilePurchase] = data?.payload

    return dispatch(
      onGetAllSanctionsSuccess({
        tenders: {
          rate: tenders.score,
          sanctions: tenders.detalleSanciones.map(sanction => {
            return {
              purchaseOrderCode: sanction.codigoexterno,
              purchaseOrderName: sanction.nombreproceso,
              organization: sanction.nombrecomprador,
              acceptanceDate:
                (sanction.fechaaceptacion && formatDate(sanction.fechaaceptacion, 'dd-MM-yyyy')) || 'Sin información',
              ID: sanction.idsancion,
              processCode: sanction.codigoProceso,
              entCodeBuyer: sanction.entcodecomprador,
              isVoid: !!sanction.sinEfecto
            }
          })
        },
        frameworkAgreement: {
          rate: frameworkAgreement.score,
          sanctions: frameworkAgreement.detalleSanciones.map(sanction => {
            return {
              purchaseOrderCode: sanction.codigoexterno,
              purchaseOrderName: sanction.nombreproceso,
              organization: sanction.nombrecomprador,
              acceptanceDate:
                (sanction.fechaaceptacion && formatDate(sanction.fechaaceptacion, 'dd-MM-yyyy')) || 'Sin información',
              ID: sanction.idsancion,
              processCode: sanction.codigoProceso,
              entCodeBuyer: sanction.entcodecomprador,
              isVoid: !!sanction.sinEfecto
            }
          })
        },
        agilePurchase: {
          rate: agilePurchase.score,
          sanctions: agilePurchase.detalleSanciones.map(sanction => {
            return {
              purchaseOrderCode: sanction.codigoexterno,
              purchaseOrderName: sanction.nombreproceso,
              organization: sanction.nombrecomprador,
              acceptanceDate:
                (sanction.fechaaceptacion && formatDate(sanction.fechaaceptacion, 'dd-MM-yyyy')) || 'Sin información',
              ID: sanction.idsancion,
              processCode: sanction.codigoProceso,
              entCodeBuyer: sanction.entcodecomprador,
              isVoid: !!sanction.sinEfecto
            }
          })
        },
        directDeals: {
          rate: directDeals.score,
          sanctions: directDeals.detalleSanciones.map(sanction => {
            return {
              purchaseOrderCode: sanction.codigoexterno,
              purchaseOrderName: sanction.nombreproceso,
              organization: sanction.nombrecomprador,
              acceptanceDate:
                (sanction.fechaaceptacion && formatDate(sanction.fechaaceptacion, 'dd-MM-yyyy')) || 'Sin información',
              ID: sanction.idsancion,
              processCode: sanction.codigoProceso,
              entCodeBuyer: sanction.entcodecomprador,
              isVoid: !!sanction.sinEfecto
            }
          })
        }
      })
    )
  } catch (error) {
    let reason
    switch (error.code) {
      case 401:
        reason = 'UNAUTHORIZED_ERROR'
        break
      case 403:
        reason = 'FORBIDDEN_ERROR'
        break
      default:
        reason = 'SOMETHING_WENT_WRONG_ERROR'
        break
    }

    return dispatch(
      onGetAllSanctionsError({
        error: customizeError({ originalError: error?.originalError, code: error?.code, reason }).toObject()
      })
    )
  }
}

export const GET_SANCTIONS_BY_PROCESS_TYPE = 'GET_SANCTIONS_BY_PROCESS_TYPE'
export const GET_SANCTIONS_BY_PROCESS_TYPE_ERROR = 'GET_SANCTIONS_BY_PROCESS_TYPE_ERROR'
export const GET_SANCTIONS_BY_PROCESS_TYPE_SUCCESS = 'GET_SANCTIONS_BY_PROCESS_TYPE_SUCCESS'
export const onGetSanctionsByProcessType = makeActionCreator(GET_SANCTIONS_BY_PROCESS_TYPE)
export const onGetSanctionsByProcessTypeError = makeActionCreator(GET_SANCTIONS_BY_PROCESS_TYPE_ERROR, 'payload')
export const onGetSanctionsByProcessTypeSuccess = makeActionCreator(GET_SANCTIONS_BY_PROCESS_TYPE_SUCCESS, 'payload')
export const onGetSanctionsByProcessTypeThunk =
  ({ dni, processType }) =>
  async dispatch => {
    dispatch(onGetSanctionsByProcessType())

    try {
      const isForeign = checkIfForeignDni(dni)
      const { data } = await getSanctionsByProcessType({
        actionType: GET_ALL_SANCTIONS,
        dni,
        isForeign: isForeign ? 1 : 0,
        processType
      })

      let processTypeName
      switch (processType) {
        case 'LC':
          processTypeName = 'tenders'
          break
        case 'CM':
          processTypeName = 'frameworkAgreement'
          break
        case 'CA':
          processTypeName = 'agilePurchase'
          break
        case 'TD':
          processTypeName = 'directDeals'
          break
      }

      const [processDetail] = data?.payload
      return dispatch(
        onGetSanctionsByProcessTypeSuccess({
          processTypeName,
          rate: processDetail?.score,
          sanctions: processDetail?.detalleSanciones.map(sanction => {
            return {
              purchaseOrderCode: sanction.codigoexterno,
              purchaseOrderName: sanction.nombreproceso,
              organization: sanction.nombrecomprador,
              acceptanceDate:
                (sanction.fechaaceptacion && formatDate(sanction.fechaaceptacion, 'dd-MM-yyyy')) || 'Sin información',
              ID: sanction.idsancion,
              processCode: sanction.codigoProceso,
              entCodeBuyer: sanction.entcodecomprador,
              isVoid: !!sanction.sinEfecto
            }
          })
        })
      )
    } catch (error) {
      let reason
      switch (error.code) {
        case 401:
          reason = 'UNAUTHORIZED_ERROR'
          break
        case 403:
          reason = 'FORBIDDEN_ERROR'
          break
        default:
          reason = 'SOMETHING_WENT_WRONG_ERROR'
          break
      }

      return dispatch(
        onGetSanctionsByProcessTypeError({
          error: customizeError({ originalError: error?.originalError, code: error?.code, reason }).toObject()
        })
      )
    }
  }
